/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import * as React from 'react';
import { useEffect, useState } from 'react';
import { ActionButton } from '@blend/enterprise-components';
import * as Ron from 'blb-ui-types/ron';
import { getUserEvents } from 'blb-api/ron';
import { UserEventType } from 'blb-constants/ron';
import * as luxon from 'luxon';
import { Box, Text } from 'grommet';
import * as Sentry from '@sentry/browser';

namespace NotarySubmitApproval {
  export interface Props {
    user?: Ron.API.UserResponse;
    onSubmitForApproval: () => Promise<void>;
  }
}
export const NotarySubmitApproval: React.FC<NotarySubmitApproval.Props> = ({ user, onSubmitForApproval }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState<boolean>();
  const [lastSubmitDate, setLastSubmitDate] = useState<string>();

  const fetchUserEvents = async () => {
    const events = await getUserEvents();
    const lastSubmit = events.find(event => event.eventType === UserEventType.SUBMIT_FOR_APPROVAL);
    setSubmitted(!!lastSubmit);
    setLastSubmitDate(lastSubmit?.createdAt);
  };

  useEffect(() => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchUserEvents();
    } catch (err) {
      Sentry.captureException(err);
    }
  }, []);

  const handleSubmitForApproval = async () => {
    try {
      setIsLoading(true);
      await onSubmitForApproval();
      await fetchUserEvents();
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return <></>;
  }

  const buttonLabel = submitted ? 'Re-submit for admin approval' : 'Submit for admin approval';
  const lastSubmittedAt = lastSubmitDate
    ? `Last submitted at ${luxon.DateTime.fromISO(lastSubmitDate).toFormat('h:m a, MMM-dd')}`
    : '';

  return (
    <Box align="center" direction="row" gap="xsmall">
      <Text color="night60" id="it-lastSubmittedAt" size="small">
        {lastSubmittedAt}
      </Text>
      <ActionButton
        btnSize="sm"
        id="it-submitApproval-btn"
        isLoading={isLoading}
        label={buttonLabel}
        onClick={handleSubmitForApproval}
        theme="tertiary"
      />
    </Box>
  );
};
